import { FormEvent, useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ErrorAlert } from '../ui/ErrorAlert';
import { extractValidationErrors, getErrorMessage } from '@/utils/errorHandler';
import type { Tag, CreateTagData } from '@/api/client';

interface TagFormProps {
    tag?: Tag;
    onSubmit: (data: CreateTagData) => Promise<void>;
    onCancel: () => void;
    isLoading?: boolean;
}

export function TagForm({ tag, onSubmit, onCancel, isLoading = false }: TagFormProps) {
    const [label, setLabel] = useState(tag?.label || '');
    const [description, setDescription] = useState(tag?.description || '');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [generalError, setGeneralError] = useState<string | null>(null);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrors({});
        setGeneralError(null);

        if (!label.trim()) {
            setErrors({ label: 'Label is required' });
            return;
        }

        try {
            const data = {
                label,
                description: description || '',
            };

            await onSubmit(data as CreateTagData);
        } catch (error) {
            const validationErrors = extractValidationErrors(error);

            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
            } else {
                setGeneralError(getErrorMessage(error));
            }
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {generalError && (
                <ErrorAlert message={generalError} onDismiss={() => setGeneralError(null)} />
            )}

            <Input
                label="Label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                error={errors.label}
                required
            />

            <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                </label>
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-base focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                {errors.description && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {errors.description}
                    </p>
                )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
                    Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={isLoading}>
                    {tag ? 'Update' : 'Create'}
                </Button>
            </div>
        </form>
    );
}
